import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useLocation } from 'react-router-dom';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { getParamFromUrl } from 'utils/helpers';
import { setBookingFormInitialValues } from 'redux/slice/experience';
import BookingForm from './booking_form';
import WarningModal from './booking_form/warning_modal';
import {
  ComplexDepartureCoach,
  ComplexDepartureFerry,
  ComplexReturnCoach,
  ComplexReturnFerry,
} from './complex_package';
import { ExperienceContext } from './helpers';
import OptionGroups from './option_groups';
import SubOptionGroups from './sub_option_groups';

/*
Terminologies to better understand this component and how it works(only valid for regular experience)

Root data source: experience

Path: /
  OptionGroups:   experience.option_groups
    OptionGroup:  OptionGroups[x]
      OptionCard: OptionGroups[x].options[x]

Path: /sub-option-groups
  SubOptionGroups:  OptionCard.option_groups
    SubOptionGroup: SubOptionGroups[x]
    SubOptionCard:  SubOptionGroups[x].options[x]

Note:

OptionGroups and SubOptionGroups page may not exist on some pages, user can goto the booking form page directly in some cases.
*/

const Experience = ({
  $experienceCanvas = document.querySelector('#experience-off-canvas'),
  productPrice,
  productSalePrice,
}) => {
  const [isCanvasOpen, setIsCanvasOpen] = useState(false);
  const [departureFerry, setDepartureFerry] = useState(null);
  const [returnFerry, setReturnFerry] = useState(null);
  const [departureCoach, setDepartureCoach] = useState(null);
  const [returnCoach, setReturnCoach] = useState(null);
  const [needUserSelect, setNeedUserSelect] = useState({
    departureFerry: false,
    returnFerry: false,
    departureCoach: false,
    returnCoach: false,
  });
  const [productFeature, setProductFeature] = useState({});
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const experience = useSelector((state) => state.experience);
  const { complex: isComplex } = experience;

  useEffect(() => {
    if ($experienceCanvas) {
      $experienceCanvas.addEventListener('shown.bs.offcanvas', () =>
        setIsCanvasOpen(true)
      );
      $experienceCanvas.addEventListener('hidden.bs.offcanvas', () =>
        setIsCanvasOpen(false)
      );
    }
    // Get the initial date value from url and set it in redux
    const { date } = getParamFromUrl();
    if (date) {
      dispatch(setBookingFormInitialValues({ date }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if ($experienceCanvas) {
      const $experienceCanvasHeader = $experienceCanvas.querySelector('div');

      // Auto scroll to the top of form when navigating
      $experienceCanvasHeader.scrollIntoView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <ExperienceContext.Provider
      value={{
        isCanvasOpen,
        departureFerry,
        setDepartureFerry,
        returnFerry,
        setReturnFerry,
        departureCoach,
        setDepartureCoach,
        returnCoach,
        setReturnCoach,
        needUserSelect,
        setNeedUserSelect,
        setProductFeature,
        productPrice,
        productSalePrice,
      }}
    >
      <>
        {!isEmpty(productFeature) && <WarningModal product={productFeature} />}
        <Switch>
          <Route path="/booking-form" component={BookingForm} />
          <Route path="/sub-option-groups" component={SubOptionGroups} />
          <Route path="/complex/return-coach" component={ComplexReturnCoach} />
          <Route
            path="/complex/departure-coach"
            component={ComplexDepartureCoach}
          />
          <Route path="/complex/return-ferry" component={ComplexReturnFerry} />
          <Route
            path="/"
            render={() =>
              isComplex ? <ComplexDepartureFerry /> : <OptionGroups />
            }
          />
        </Switch>
      </>
    </ExperienceContext.Provider>
  );
};

Experience.propTypes = {
  $experienceCanvas: PropTypes.instanceOf(Element),
  productPrice: PropTypes.number,
  productSalePrice: PropTypes.number,
};

export default Experience;
